import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../../../reduxService/hooks';
import { clearLastSubmitResult, clearSubmissionErrors, setAccordionToOpen, selectSubmissionErrors } from '../../../../../store/form/formControlSlice';
import type { SubmitSource } from '../../../../../store/form/formControlSlice';
import { logDebug, logError, logWarn, LogTag } from '../../../../../utils/utils';
import { IGeogridData } from '../../../../../api/IApiResponse';
import { FormStateManagementService } from '../../../../../services/FormStateManagementService';
import { getRunErrorMessage } from '../../../../../utils/runErrorMessage';

export interface UseStepperErrorHandlingProps {
    setErrorDialogContent: (content: React.ReactNode | null) => void;
    handleFormSubmitSuccess: (structuredData: Record<string, unknown>, shouldNavigate: boolean, source?: SubmitSource) => void;
    handleGeoGridRunSuccess: (geoGridData: IGeogridData) => void;
    setIsApiLoading: (loading: boolean) => void;
}

export interface UseStepperErrorHandlingReturn {
    handleFormSubmitFailure: (errorPayload: unknown) => void;
    handleErrorDialogOk: () => void;
    handleDialogCloseOnly: () => void;
}

/**
 * Utility function to extract error messages from validation errors
 */
const extractErrorMessages = (errors: unknown, pathPrefix = ''): string[] => {
    let messages: string[] = [];
    if (!errors) return messages;

    if (typeof errors === 'string' && errors) {
        const displayPath = pathPrefix
            .replace(/geogrids\[(\d+)\]/g, (match, p1) => `Geogrid Layer ${parseInt(p1, 10) + 1}`)
            .replace(/_/g, ' ')
            .replace(/\b[a-z]/g, char => char.toUpperCase())
            .replace(/\./g, ' -> ');

        const messageText = displayPath ? `${displayPath}: ${errors}` : errors;
        messages.push(messageText);
    } else if (Array.isArray(errors)) {
        errors.forEach((item, index) => {
            if (item) {
                messages = messages.concat(extractErrorMessages(item, `${pathPrefix}[${index}]`));
            }
        });
    } else if (typeof errors === 'object' && errors !== null) {
        Object.entries(errors).forEach(([key, value]) => {
            const newPath = pathPrefix ? `${pathPrefix}.${key}` : key;
            messages = messages.concat(extractErrorMessages(value, newPath));
        });
    }

    return messages;
};

/**
 * Hook for managing stepper error handling and form submission lifecycle
 * Handles error dialogs, form submission success/failure, and focus management
 */
export const useStepperErrorHandling = ({ setErrorDialogContent, handleFormSubmitSuccess, handleGeoGridRunSuccess, setIsApiLoading }: UseStepperErrorHandlingProps): UseStepperErrorHandlingReturn => {
    const dispatch = useAppDispatch();
    const location = useLocation();

    // Redux selectors
    const lastSubmitResult = useAppSelector(state => state.formControl.lastSubmitResult);
    const submissionErrorsForFocus = useAppSelector(selectSubmissionErrors);

    /**
     * Handles form submission failures by parsing errors and displaying appropriate dialog
     */
    const handleFormSubmitFailure = useCallback(
        (errorPayload: unknown) => {
            logError('Stepper', 'Handling form submit failure. Raw error payload received:', errorPayload, LogTag.FORM_SUBMISSION);

            const validationErrors = errorPayload && typeof errorPayload === 'object' && 'validationErrors' in errorPayload ? (errorPayload as Record<string, unknown>).validationErrors : null;

            logDebug('Stepper', 'Extracted Validation errors:', validationErrors, LogTag.FORM_VALIDATION);

            const errorMessages = extractErrorMessages(validationErrors);

            if (errorMessages && errorMessages.length > 0) {
                return;
            }
            const submissionError = errorPayload && typeof errorPayload === 'object' && 'error' in errorPayload ? errorPayload.error : errorPayload;
            setErrorDialogContent(getRunErrorMessage(typeof submissionError === 'string' ? { message: submissionError } : submissionError, 'submission'));
        },
        [setErrorDialogContent]
    );

    /**
     * Internal function to handle focus management for validation errors
     * Separated from dialog logic to allow automatic triggering
     */
    const processValidationErrors = useCallback(() => {
        logDebug('Stepper', 'processValidationErrors called.', undefined, LogTag.UI_INTERACTIONS);
        const errorsForFocus = submissionErrorsForFocus;
        logDebug('Stepper', 'Errors read from Redux state for focus:', JSON.stringify(errorsForFocus), LogTag.FORM_VALIDATION);

        if (errorsForFocus && typeof errorsForFocus === 'object' && Object.keys(errorsForFocus).length > 0) {
            let firstFieldName: string | null = null;
            let groupNameToOpen: string | null = null;

            const findFirstError = (currentErrors: unknown, currentPath = ''): boolean => {
                if (!currentErrors) return false;

                if (typeof currentErrors === 'string') {
                    firstFieldName = currentPath;
                    groupNameToOpen = currentPath.split('.')[0].split('[')[0];
                    return true;
                }

                if (Array.isArray(currentErrors)) {
                    for (let i = 0; i < currentErrors.length; i++) {
                        if (findFirstError(currentErrors[i], `${currentPath}[${i}]`)) {
                            return true;
                        }
                    }
                } else if (typeof currentErrors === 'object' && currentErrors !== null) {
                    const errorObj = currentErrors as Record<string, unknown>;
                    for (const key in errorObj) {
                        if (Object.prototype.hasOwnProperty.call(errorObj, key)) {
                            if (findFirstError(errorObj[key], currentPath ? `${currentPath}.${key}` : key)) {
                                return true;
                            }
                        }
                    }
                }
                return false;
            };

            findFirstError(errorsForFocus);

            if (groupNameToOpen) {
                logDebug('Stepper', `First field name found: ${firstFieldName}, Group: ${groupNameToOpen}`, undefined, LogTag.FORM_VALIDATION);
                logDebug('Stepper', 'Dispatching setAccordionToOpen:', groupNameToOpen, LogTag.UI_INTERACTIONS);
                dispatch(setAccordionToOpen(groupNameToOpen));
            } else {
                logDebug('Stepper', 'No specific field path found in errors object.', undefined, LogTag.FORM_VALIDATION);
            }

            requestAnimationFrame(() => {
                logDebug('Stepper', 'rAF executing: Attempting focus...', undefined, LogTag.UI_INTERACTIONS);
                if (firstFieldName) {
                    const elementToFocus = document.querySelector(`[name="${firstFieldName}"]`);
                    if (elementToFocus instanceof HTMLElement) {
                        logDebug('Stepper', `Focusing and scrolling to: ${firstFieldName}`, undefined, LogTag.UI_INTERACTIONS);
                        elementToFocus.focus({ preventScroll: false });
                        elementToFocus.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    } else {
                        logWarn('Stepper', `Could not find focusable element: ${firstFieldName}`, undefined, LogTag.UI_INTERACTIONS);
                    }
                } else {
                    logDebug('Stepper', 'No field name found to focus in rAF.', undefined, LogTag.UI_INTERACTIONS);
                }
                logDebug('Stepper', 'Clearing accordionToOpen trigger in rAF.', undefined, LogTag.UI_INTERACTIONS);
                dispatch(setAccordionToOpen(null));
            });
        } else {
            logDebug('Stepper', 'No structured errors found in Redux state to process for focus.', undefined, LogTag.FORM_VALIDATION);
            dispatch(setAccordionToOpen(null));
            dispatch(clearSubmissionErrors());
        }
    }, [dispatch, submissionErrorsForFocus]);

    /**
     * Handles error dialog OK button click with focus management
     */
    const handleErrorDialogOk = useCallback(() => {
        logDebug('Stepper', 'handleErrorDialogOk called.', undefined, LogTag.UI_INTERACTIONS);
        setErrorDialogContent(null);
        processValidationErrors();
    }, [setErrorDialogContent, processValidationErrors]);

    /**
     * Handles dialog close without focus management
     */
    const handleDialogCloseOnly = useCallback(() => {
        logDebug('Stepper', 'handleDialogCloseOnly called.', undefined, LogTag.UI_INTERACTIONS);
        setErrorDialogContent(null);
        dispatch(clearSubmissionErrors());
        dispatch(setAccordionToOpen(null));
    }, [dispatch, setErrorDialogContent]);

    /**
     * Effect to handle form submission results from Redux
     * 🚀 ENHANCED: Skip save operation for design-only changes to prevent "project saved" popup
     */
    useEffect(() => {
        if (!lastSubmitResult) return;

        logDebug('Stepper', 'Detected lastSubmitResult:', lastSubmitResult, LogTag.FORM_SUBMISSION);
        const source = lastSubmitResult.source || 'Unknown';

        if (lastSubmitResult.success) {
            logDebug('Stepper', `Processing submit success from source: ${source}`, undefined, LogTag.FORM_SUBMISSION);
            setErrorDialogContent(null);
            const isStandardRun = source === 'StepperRun' || source === 'ButtonExecute';
            const isGeoGridRun = source === 'StepperGeoGridRun';

            if (isGeoGridRun) {
                handleGeoGridRunSuccess(lastSubmitResult.data as IGeogridData);
            } else if (isStandardRun) {
                const isAlreadyOnResults = location.pathname.includes('/results');
                const shouldNavigate = !isAlreadyOnResults;
                logDebug('Stepper', `Navigation check: shouldNavigate=${shouldNavigate} (isAlreadyOnResults=${isAlreadyOnResults})`, undefined, LogTag.NAVIGATION);
                handleFormSubmitSuccess(lastSubmitResult.data as Record<string, unknown>, shouldNavigate, source);
            } else if (source === 'ButtonSave') {
                // An explicit Save persists even a design-only change: the autosubmit check below is not for it
                handleFormSubmitSuccess(lastSubmitResult.data as Record<string, unknown>, false, source);
            } else if (source === 'ExecuteSync') {
                // Formik→Redux sync of the Paved/Airport Execute: the orchestrator saves once after the calculation.
                logDebug('Stepper', 'Skipping save for the Execute sync', undefined, LogTag.FORM_SUBMISSION);
            } else {
                // 🚀 CHECK: For "Unknown" source, check if it's a design-only change
                // Design-only changes should NOT trigger save + "project saved" popup
                // They only need to update calculations (which happens automatically via Redux)
                const formStateService = FormStateManagementService.getInstance();
                const shouldAutoSubmit = formStateService.shouldAutoSubmit();

                if (shouldAutoSubmit) {
                    logDebug(
                        'Stepper',
                        '✅ SKIPPING SAVE: Design-only change detected - calculations will update automatically without save popup',
                        { source, shouldAutoSubmit },
                        LogTag.FORM_SUBMISSION
                    );
                    // Don't call handleFormSubmitSuccess - just clear the result
                    // This prevents the save API call and "project saved" popup
                } else {
                    // Normal submit from unknown source - proceed with save
                    // Keep original source ('Unknown') - alert will NOT be shown for non-ButtonSave sources
                    logDebug('Stepper', 'Processing normal submit from unknown source', { source }, LogTag.FORM_SUBMISSION);
                    handleFormSubmitSuccess(lastSubmitResult.data as Record<string, unknown>, false, source);
                }
            }
        } else {
            setIsApiLoading(false);
            logError('Stepper', `Processing submit failure from source: ${source}`, undefined, LogTag.FORM_SUBMISSION);

            handleFormSubmitFailure(lastSubmitResult.error);
            processValidationErrors();
        }
        dispatch(clearLastSubmitResult());
    }, [lastSubmitResult, dispatch, location, handleFormSubmitSuccess, handleGeoGridRunSuccess, setErrorDialogContent, processValidationErrors, handleFormSubmitFailure, setIsApiLoading]);

    return {
        handleFormSubmitFailure,
        handleErrorDialogOk,
        handleDialogCloseOnly,
    };
};
